import { isBuildingTile, type Tile } from "../engine";
import { resolveRoadAutotileMaskWithBuildings } from "./roadBuildingAutotile";
import { ROAD_CONNECT_E, ROAD_CONNECT_N, ROAD_CONNECT_S, ROAD_CONNECT_W } from "./roadConnectFlags";

export { ROAD_CONNECT_E, ROAD_CONNECT_N, ROAD_CONNECT_S, ROAD_CONNECT_W } from "./roadConnectFlags";

function isRoadTile(tile: Tile | undefined): boolean {
  return tile?.kind === "road";
}

function isSeaTile(tile: Tile | undefined): boolean {
  return tile?.kind === "sea";
}

function cardinalNeighbors({
  tiles,
  width,
  x,
  y,
}: {
  tiles: readonly Tile[];
  width: number;
  x: number;
  y: number;
}): {
  north: Tile | undefined;
  east: Tile | undefined;
  south: Tile | undefined;
  west: Tile | undefined;
} {
  const height = tiles.length / width;
  return {
    north: y > 0 ? tiles[(y - 1) * width + x] : undefined,
    east: x < width - 1 ? tiles[y * width + (x + 1)] : undefined,
    south: y < height - 1 ? tiles[(y + 1) * width + x] : undefined,
    west: x > 0 ? tiles[y * width + (x - 1)] : undefined,
  };
}

function isBuildingConnectable(tile: Tile | undefined): boolean {
  return tile !== undefined && isBuildingTile(tile.kind);
}

function connectionCount(mask: number): number {
  let count = 0;
  if (mask & ROAD_CONNECT_N) count += 1;
  if (mask & ROAD_CONNECT_E) count += 1;
  if (mask & ROAD_CONNECT_S) count += 1;
  if (mask & ROAD_CONNECT_W) count += 1;
  return count;
}

function isLandNeighbor(tile: Tile | undefined): boolean {
  return tile !== undefined && !isSeaTile(tile) && !isRoadTile(tile);
}

function extendBorderDeadEndToStraight({
  mask,
  x,
  y,
  width,
  height,
}: {
  mask: number;
  x: number;
  y: number;
  width: number;
  height: number;
}): number {
  if (connectionCount(mask) !== 1) return mask;

  if (y === 0 && mask & ROAD_CONNECT_S) return mask | ROAD_CONNECT_N;
  if (y === height - 1 && mask & ROAD_CONNECT_N) return mask | ROAD_CONNECT_S;
  if (x === 0 && mask & ROAD_CONNECT_E) return mask | ROAD_CONNECT_W;
  if (x === width - 1 && mask & ROAD_CONNECT_W) return mask | ROAD_CONNECT_E;

  return mask;
}

function extendLandAbuttingWaterDeadEndToStraight({
  mask,
  tiles,
  width,
  x,
  y,
}: {
  mask: number;
  tiles: readonly Tile[];
  width: number;
  x: number;
  y: number;
}): number {
  const count = connectionCount(mask);
  if (count > 1) return mask;

  const { north, east, south, west } = cardinalNeighbors({ tiles, width, x, y });

  if (isSeaTile(north) && isSeaTile(south)) {
    let next = mask;
    if ((count === 0 || mask & ROAD_CONNECT_W) && isLandNeighbor(east)) next |= ROAD_CONNECT_E;
    if ((count === 0 || mask & ROAD_CONNECT_E) && isLandNeighbor(west)) next |= ROAD_CONNECT_W;
    return next;
  }

  if (isSeaTile(east) && isSeaTile(west)) {
    let next = mask;
    if ((count === 0 || mask & ROAD_CONNECT_N) && isLandNeighbor(south)) next |= ROAD_CONNECT_S;
    if ((count === 0 || mask & ROAD_CONNECT_S) && isLandNeighbor(north)) next |= ROAD_CONNECT_N;
    return next;
  }

  return mask;
}

export function seaNeighborMask({
  tiles,
  width,
  x,
  y,
}: {
  tiles: readonly Tile[];
  width: number;
  x: number;
  y: number;
}): number {
  const { north, east, south, west } = cardinalNeighbors({ tiles, width, x, y });
  let mask = 0;

  if (isSeaTile(north)) mask |= ROAD_CONNECT_N;
  if (isSeaTile(east)) mask |= ROAD_CONNECT_E;
  if (isSeaTile(south)) mask |= ROAD_CONNECT_S;
  if (isSeaTile(west)) mask |= ROAD_CONNECT_W;

  return mask;
}

export function roadNeighborMask({
  tiles,
  width,
  x,
  y,
}: {
  tiles: readonly Tile[];
  width: number;
  x: number;
  y: number;
}): number {
  const { north, east, south, west } = cardinalNeighbors({ tiles, width, x, y });

  let mask = 0;
  if (isRoadTile(north)) mask |= ROAD_CONNECT_N;
  if (isRoadTile(east)) mask |= ROAD_CONNECT_E;
  if (isRoadTile(south)) mask |= ROAD_CONNECT_S;
  if (isRoadTile(west)) mask |= ROAD_CONNECT_W;
  return mask;
}

function buildingNeighborMask({
  tiles,
  width,
  x,
  y,
}: {
  tiles: readonly Tile[];
  width: number;
  x: number;
  y: number;
}): number {
  const { north, east, south, west } = cardinalNeighbors({ tiles, width, x, y });

  let mask = 0;
  if (isBuildingConnectable(north)) mask |= ROAD_CONNECT_N;
  if (isBuildingConnectable(east)) mask |= ROAD_CONNECT_E;
  if (isBuildingConnectable(south)) mask |= ROAD_CONNECT_S;
  if (isBuildingConnectable(west)) mask |= ROAD_CONNECT_W;
  return mask;
}

export function resolveRoadAutotileMask({
  tiles,
  width,
  x,
  y,
}: {
  tiles: readonly Tile[];
  width: number;
  x: number;
  y: number;
}): number {
  const height = tiles.length / width;
  const roadMask = roadNeighborMask({ tiles, width, x, y });
  const roadCount = connectionCount(roadMask);
  const buildingMask = buildingNeighborMask({ tiles, width, x, y });

  if (roadCount >= 2) return roadMask;

  const mask = resolveRoadAutotileMaskWithBuildings({ roadMask, buildingMask }) ?? roadMask;
  const withBorder = extendBorderDeadEndToStraight({ mask, x, y, width, height });
  return extendLandAbuttingWaterDeadEndToStraight({
    mask: withBorder,
    tiles,
    width,
    x,
    y,
  });
}

export function roadConnectivityMask({
  tiles,
  width,
  x,
  y,
}: {
  tiles: readonly Tile[];
  width: number;
  x: number;
  y: number;
}): number {
  const { north, east, south, west } = cardinalNeighbors({ tiles, width, x, y });

  const roadNeighborCount = [north, east, south, west].filter(isRoadTile).length;
  const canConnectToBuilding = roadNeighborCount <= 1;

  let mask = 0;
  if (isRoadTile(north)) mask |= ROAD_CONNECT_N;
  else if (canConnectToBuilding && isBuildingConnectable(north)) mask |= ROAD_CONNECT_N;

  if (isRoadTile(east)) mask |= ROAD_CONNECT_E;
  else if (canConnectToBuilding && isBuildingConnectable(east)) mask |= ROAD_CONNECT_E;

  if (isRoadTile(south)) mask |= ROAD_CONNECT_S;
  else if (canConnectToBuilding && isBuildingConnectable(south)) mask |= ROAD_CONNECT_S;

  if (isRoadTile(west)) mask |= ROAD_CONNECT_W;
  else if (canConnectToBuilding && isBuildingConnectable(west)) mask |= ROAD_CONNECT_W;

  return mask;
}
